import {Stack, Typography} from '@mui/material';
import type jsPDF from 'jspdf';
import {useMemo, useState} from 'react';
import {useModalBtns} from './use-modal-btns';
import {wait} from '../../utils/functions/wait';
import {Modal} from './modal';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {PdfViewer} from '../pdf-viewer/pdf-viewer';
import {useAppContext} from '../../contexts/app/context';

type ModalPdfProps = {
  open: boolean;
  onClose?: () => void;
  pdf?: jsPDF;
  saveName?: string;
};

export function ModalPdf({open, onClose, pdf, saveName}: ModalPdfProps) {
  const {t} = useTranslation();
  const appContext = useAppContext();
  const [loading, setLoading] = useState(false);
  const actionBtns = useModalBtns({
    left: {
      type: 'cancel',
      onClick: () => {
        onClose?.();
      },
    },
    // When launched from the social app the user is "locked" (no access to OS features such as
    // downloaded files), so saving is not available
    right: appContext.state.runningFromSocialApp
      ? undefined
      : {
          type: 'save',
          disabled: !pdf,
          onClick: async () => {
            try {
              if (!pdf) throw new Error('PDF not available');
              setLoading(true);
              pdf.save(saveName ? `${saveName}.pdf` : `${pdf.getFileId()}.pdf`);
              await wait(1000);
              onClose?.();
            } finally {
              setLoading(false);
            }
          },
          loading: loading,
        },
  });

  const pdfData = useMemo(() => pdf?.output('arraybuffer'), [pdf]);

  return (
    <Modal
      preventCloseClick
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      actions={actionBtns}
    >
      <Stack
        sx={{
          placeContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '80vh',
        }}
      >
        {pdfData ? (
          <PdfViewer data={pdfData} />
        ) : (
          <Stack justifyContent="center" alignItems="center">
            <Typography variant="h6">{t(translations.loading_content.loading)}</Typography>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
